<template>
  <div class="site-header">
    <div
      class="top bg-bg-5 margin-s-bottom padding-m-vertical l:padding-l-vertical flex-middle flex-between gap-xl">
      <div class="logo-container relative">
        <div class="logo-container-inner">
          <div class="logo-bottom-graphic"></div>
          <img
            class="logo-image cursor-pointer"
            :src="getDynamicAssetUrl('core/images/logos/logo.png')"
            :alt="t('ag.header.logoAlt')"
            @click="$router.push({ name: 'index' })" />
        </div>
      </div>
      <div class="main-actions-container flex-middle flex-center gap-l">
        <div class="input-external-wrapper relative width-expand max-width-400">
          <img
            class="search-icon"
            :src="getDynamicAssetUrl('images/header/ag-search-icon.svg')"
            :alt="t('ag.header.searchPlaceholder')" />
          <primary-search
            :field-props="{
              class: 'width-expand max-width-400',
              size: 's',
              label: false,
              icon: false,
            }"></primary-search>
        </div>

        <div class="flex gap-m l:visible">
          <a
            href="https://buymeacoffee.com/danea"
            target="_blank"
            class="minimal-link inline-block">
            <form-button
              size="s"
              class="text-bold"
              theme="alt-inverse"
              icon-end="coffee-cup"
              text="ag.header.buyMeCoffee"></form-button>
          </a>

          <form-button
            size="s"
            class="text-bold"
            theme="alt-inverse"
            text="ag.header.joinNewsletter"
            @click="startNewsletterRegistration"></form-button>
        </div>
      </div>
      <div class="extras-container s:visible">
        <div class="flex gap-m flex-end">
          <saffron-link
            class="plain-link block"
            :to="{
              name: 'content-article-articleAlias',
              params: { articleAlias: 'english' },
            }"
            style="line-height: 0">
            <img :src="getDynamicAssetUrl('images/header/uk-flag.svg')" />
          </saffron-link>
          <saffron-link class="plain-link block" :to="'/'" style="line-height: 0">
            <img :src="getDynamicAssetUrl('images/header/facebook-logo.svg')" />
          </saffron-link>
        </div>
      </div>
    </div>
    <div class="bottom bg-alt-1 padding-s-vertical l:padding-m-vertical">
      <div class="">
        <nav class="l:visible container container-l">
          <ul class="unstyled-list main-header-nav-desktop">
            <li>
              <saffron-link :to="{ name: 'index' }" class="strict-active-visual">
                <span>{{ tu("ag.header.nav.home") }}</span>
              </saffron-link>
            </li>
            <li>
              <saffron-link :to="{ name: 'content-article', params: {} }">
                <span>{{ tu("ag.header.nav.articles") }}</span>
              </saffron-link>
            </li>
            <li>
              <saffron-link :to="{ name: 'content-term', params: {} }">
                <span>{{ tu("ag.header.nav.terms") }}</span>
              </saffron-link>
            </li>
            <li>
              <saffron-link :to="{name: 'store'}">
                <span>{{ tu("ag.header.nav.store") }}</span>
              </saffron-link>
            </li>
            <li>
              <a href="#" @click.prevent.stop="startNewsletterRegistration">
                <span>{{ tu("ag.header.nav.newsletter") }}</span>
              </a>
            </li>
            <li>
              <saffron-link
                :to="{name: 'content-link-alias', params: {alias: 'general'}}">
                <span>{{ tu("ag.header.nav.links") }}</span>
              </saffron-link>
            </li>
            <li>
              <saffron-link
                :to="{
                  name: 'content-article-articleAlias',
                  params: { articleAlias: 'about-us' },
                }">
                <span>{{ tu("ag.header.nav.about") }}</span>
              </saffron-link>
            </li>
            <li>
              <saffron-link :to="{ name: 'leads-basic-contact' }">
                <span>{{ tu("ag.header.nav.contact") }}</span>
              </saffron-link>
            </li>
          </ul>
        </nav>
        <div class="l:hidden flex">
          <div class="spacer mobile-nav-control-spacer"></div>
          <div
            class="mobile-nav-control width-expand flex-center flex-middle c-gray-4 text-semibold">
            <div class="mobile-nav-control-inner flex-middle cursor-pointer gap-s">
              <icon-text
                :icon="isMobileMenuExpanded ? 'x' : 'align-justify-alt'"
                text="ag.header.menu"
                @click="isMobileMenuExpanded = !isMobileMenuExpanded"></icon-text>
            </div>
          </div>
        </div>
      </div>
    </div>
    <drawer
      v-model="isMobileMenuExpanded"
      class="mobile-nav-drawer"
      title="ag.header.siteName">
      <template #header-content>
        <div class="logo width-expand margin-inline-auto flex-middle flex-center">
          <img
            class="logo-image"
            :src="getDynamicAssetUrl('core/images/logos/logo.png')"
            :alt="t('ag.header.logoAlt')" />
        </div>
      </template>
      <template #default>
        <div class="mobile-menu-wrapper flex-column gap-l">
          <nav class="">
            <ul class="unstyled-list main-header-nav-mobile">
              <li>
                <saffron-link :to="{ name: 'index' }" class="strict-active-visual">
                  <span>{{ tu("ag.header.nav.home") }}</span>
                </saffron-link>
              </li>
              <li>
                <saffron-link :to="{ name: 'content-article', params: {} }">
                  <span>{{ tu("ag.header.nav.articles") }}</span>
                </saffron-link>
              </li>
              <li>
                <saffron-link :to="{ name: 'content-term', params: {} }">
                  <span>{{ tu("ag.header.nav.terms") }}</span>
                </saffron-link>
              </li>
              <li>
                <saffron-link :to="{name: 'store'}">
                  <span>{{ tu("ag.header.nav.store") }}</span>
                </saffron-link>
              </li>
              <li>
                <a
                  href="#"
                  class="cursor-pointer"
                  @click.prevent.stop="startNewsletterRegistration">
                  <span>{{ tu("ag.header.nav.newsletter") }}</span>
                </a>
              </li>
              <li>
                <saffron-link
                  :to="{name: 'content-link-alias', params: {alias: 'general'}}">
                  <span>{{ tu("ag.header.nav.links") }}</span>
                </saffron-link>
              </li>
              <li>
                <saffron-link
                  :to="{
                    name: 'content-article-articleAlias',
                    params: { articleAlias: 'about-us' },
                  }">
                  <span>{{ tu("ag.header.nav.about") }}</span>
                </saffron-link>
              </li>
              <li>
                <saffron-link :to="{ name: 'leads-basic-contact' }">
                  <span>{{ tu("ag.header.nav.contact") }}</span>
                </saffron-link>
              </li>
            </ul>
          </nav>
          <hr style="flex: 1px; width: 100%" />
          <div class="flex-column gap-m">
            <a
              href="https://buymeacoffee.com/danea"
              target="_blank"
              class="minimal-link inline-block">
              <form-button
                size="m"
                class="text-bold width-expand"
                theme="alt-inverse"
                icon-end="coffee-cup"
                text="ag.header.buyMeCoffee"></form-button>
            </a>
            <form-button
              size="m"
              class="text-bold width-expand"
              theme="alt-inverse"
              text="ag.header.joinNewsletter"
              @click.prevent.stop="startNewsletterRegistration"></form-button>
          </div>
          <div class="flex gap-l flex-center">
            <saffron-link
              class="plain-link block"
              :to="{
                name: 'content-article-articleAlias',
                params: { articleAlias: 'english' },
              }"
              style="line-height: 0">
              <img :src="getDynamicAssetUrl('images/header/uk-flag.svg')" />
            </saffron-link>
            <a href="#" _target="blank" class="plain-link block" style="line-height: 0">
              <img :src="getDynamicAssetUrl('images/header/facebook-logo.svg')" />
            </a>
          </div>
        </div>
      </template>
    </drawer>
    <news-letter-registration
      ref="newsLetterRegistrationComponent"></news-letter-registration>
  </div>
</template>

<script>
import PrimarySearch from "@/client/components/etc/PrimarySearch.vue";
import NewsLetterRegistration from "@/client/applications/agaloola/components/etc/NewsLetterRegistration.vue";

export default {
  components: {
    NewsLetterRegistration,
    PrimarySearch,
  },
  setup() {
    return {};
  },
  data: function () {
    return {
      isMobileMenuExpanded: false,
    };
  },
  watch: {
    $route: {
      handler() {
        this.isMobileMenuExpanded = false;
      },
      deep: true,
    },
  },
  methods: {
    startNewsletterRegistration(modalErrorText = undefined) {
      this.$refs["newsLetterRegistrationComponent"].startNewsletterRegistration(
        modalErrorText
      );
    },
  },
};
</script>

<style scoped lang="scss">
.site-header {
  border-bottom: 1px solid var(--c-gray-1);
  box-shadow: 0 4px 4px 0 rgba(0, 0, 0, 0.25);
}

.top,
.bottom {
  padding-inline: var(--margin-xl);
  @media (max-width: 1100px) {
    padding-inline: var(--container-gutter);
  }
}
.top {
  //display: flex;
  // gap: var(--margin-xl);

  .logo-container {
    flex: 0 0 200px;

    @media (max-width: 1024px) {
      flex: 0 0 120px;
    }

    .logo-container-inner {
      position: absolute;
      display: flex;
      justify-content: center;
      top: calc(1em * -1);
      padding: 0 var(--margin-l) var(--margin-l) var(--margin-l);
      inset-inline-start: 0;
      background-color: var(--c-bg-5);
      border-radius: 50px;
      width: 100%;

      @media (max-width: 1024px) {
        padding: 0 var(--margin-m) var(--margin-m) var(--margin-m);
        border-radius: 30px;
      }
    }

    .logo-image {
      position: relative;
      z-index: 2;
      margin-inline: auto;

      @media (max-width: 1024px) {
        position: relative;
        top: 10px;
        width: 90px;
        height: auto;
      }
    }

    .logo-bottom-graphic {
      position: absolute;
      bottom: -6px;
      z-index: 1;
      inset-inline-start: 0;
      background: var(--c-bg-5);
      height: calc(var(--base-margin) * 47);
      width: 100%;
      border: var(--margin-s) solid var(--c-bg-1);
      border-top: none;
      border-start-start-radius: 0;
      border-start-end-radius: 0;
      border-end-start-radius: 50px;
      border-end-end-radius: 50px;

      @media (max-width: 1024px) {
        height: calc(var(--base-margin) * 37);
        border-end-start-radius: 30px;
        border-end-end-radius: 30px;
        bottom: -26px;
      }
    }
  }

  .main-actions-container {
    width: 100%;

    :deep(.field__element) {
      padding-block: 0.6em;
      padding-inline-start: 4em;

      @media (max-width: 767px) {
        min-width: 100px;
      }
    }

    .input-external-wrapper {
      .search-icon {
        position: absolute;
        top: 50%;
        transform: translateY(-50%);
        inset-inline-start: var(--margin-m);
        z-index: var(--z-2);
      }
    }

    .button {
      font-size: var(--font-s);
      color: var(--c-gray-4);
      height: 2.4em;
      min-height: 2.4em;
    }
  }

  .extras-container {
    flex: 0 0 200px;

    @media (max-width: 767px) {
      flex: 0 0 60px;
    }
    img {
      height: 33px;
      width: auto;

      @media (max-width: 767px) {
        height: 22px;
      }
    }
  }
}
.main-header-nav-desktop {
  display: flex;
  justify-content: center;
  gap: calc(var(--margin-2xl) * 1.1);

  @media (max-width: 1200px) {
    gap: calc(var(--margin-2xl) * 0.8);
  }

  li {
    a {
      display: inline-block;
      font-weight: 600;
      font-size: var(--text-l);
      color: var(--c-gray-4);
      transition: color 150ms ease;

      &:hover,
      &:hover span {
        text-decoration: none;
      }

      &:hover:not(.active),
      &:hover:not(.active) span {
        color: var(--c-lead);
        text-decoration: none;
      }

      &.active:not(.strict-active-visual),
      &.active:not(.strict-active-visual) span {
        color: var(--c-lead-4);

        &:hover {
          cursor: default;
        }
      }

      &.router-link-exact-active,
      &.router-link-exact-active span {
        color: var(--c-lead-4);

        &:hover {
          cursor: default;
        }
      }
    }
  }
}
.mobile-nav-control-spacer {
  // this is logo size with decoration + margin from search
  flex: 0 0 calc(130px + var(--margin-l));
}

.main-header-nav-mobile {
  display: flex;
  flex-direction: column;
  gap: var(--margin-m);

  li {
    a {
      display: block;
      background: var(--c-alt-1);
      border-radius: var(--border-radius);
      padding: var(--margin-m) var(--margin-l);
      color: var(--c-text);
      font-size: var(--text-l);
      font-weight: 500;

      &.active:not(.strict-active-visual),
      &.active:not(.strict-active-visual) span {
        background: var(--c-lead-2);
      }

      &.router-link-exact-active {
        background: var(--c-lead-2);
      }
    }
  }
}
</style>
<style lang="scss">
.mobile-nav-drawer {
  .drawer-header {
    margin-bottom: var(--margin-l) !important;
  }
}
</style>
